import * as React from 'react';
import { cn } from '../../utils/cn';
import { Button } from '../Button/Button';

export interface ErrorAction {
  label: string;
  href?: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  icon?: React.ReactNode;
}

export interface ErrorPageProps {
  code?: 404 | 500 | 403 | 401 | number;
  title?: string;
  description?: string;
  illustration?: React.ReactNode;
  actions?: ErrorAction[];
  showDetails?: boolean;
  error?: Error;
  className?: string;
}

// Default illustrations as simple SVG components
const NotFoundIllustration = () => (
  <svg className="w-64 h-64" viewBox="0 0 256 256" fill="none">
    <circle cx="128" cy="128" r="96" stroke="currentColor" strokeWidth="8" opacity="0.2" />
    <path
      d="M88 104C88 95.1634 95.1634 88 104 88H152C160.837 88 168 95.1634 168 104V104C168 112.837 160.837 120 152 120H104C95.1634 120 88 112.837 88 104V104Z"
      fill="currentColor"
      opacity="0.3"
    />
    <rect x="88" y="136" width="80" height="16" rx="8" fill="currentColor" opacity="0.3" />
    <rect x="104" y="168" width="48" height="16" rx="8" fill="currentColor" opacity="0.3" />
  </svg>
);

const ServerErrorIllustration = () => (
  <svg className="w-64 h-64" viewBox="0 0 256 256" fill="none">
    <rect
      x="64"
      y="80"
      width="128"
      height="96"
      rx="8"
      stroke="currentColor"
      strokeWidth="8"
      opacity="0.2"
    />
    <circle cx="88" cy="104" r="8" fill="currentColor" opacity="0.5" />
    <circle cx="112" cy="104" r="8" fill="currentColor" opacity="0.5" />
    <circle cx="136" cy="104" r="8" fill="currentColor" opacity="0.5" />
    <path
      d="M88 128H168M88 144H168M88 160H168"
      stroke="currentColor"
      strokeWidth="8"
      strokeLinecap="round"
      opacity="0.3"
    />
  </svg>
);

const ForbiddenIllustration = () => (
  <svg className="w-64 h-64" viewBox="0 0 256 256" fill="none">
    <circle cx="128" cy="128" r="96" stroke="currentColor" strokeWidth="8" opacity="0.2" />
    <path
      d="M128 88V144M128 168V176"
      stroke="currentColor"
      strokeWidth="16"
      strokeLinecap="round"
      opacity="0.5"
    />
  </svg>
);

const UnauthorizedIllustration = () => (
  <svg className="w-64 h-64" viewBox="0 0 256 256" fill="none">
    <rect
      x="88"
      y="96"
      width="80"
      height="96"
      rx="8"
      stroke="currentColor"
      strokeWidth="8"
      opacity="0.2"
    />
    <circle cx="128" cy="120" r="16" fill="currentColor" opacity="0.3" />
    <path
      d="M112 144C112 135.163 119.163 128 128 128V128C136.837 128 144 135.163 144 144V168"
      stroke="currentColor"
      strokeWidth="8"
      strokeLinecap="round"
      opacity="0.3"
    />
  </svg>
);

const errorTemplates = {
  404: {
    title: 'Page Not Found',
    description: "The page you're looking for doesn't exist or has been moved.",
    illustration: <NotFoundIllustration />,
    actions: [
      { label: 'Go Home', href: '/', variant: 'primary' as const },
      { label: 'Go Back', onClick: () => window.history.back(), variant: 'secondary' as const },
    ],
  },
  500: {
    title: 'Something Went Wrong',
    description: "We're having technical difficulties. Please try again later.",
    illustration: <ServerErrorIllustration />,
    actions: [
      { label: 'Retry', onClick: () => window.location.reload(), variant: 'primary' as const },
      { label: 'Go Home', href: '/', variant: 'secondary' as const },
    ],
  },
  403: {
    title: 'Access Denied',
    description: "You don't have permission to view this page.",
    illustration: <ForbiddenIllustration />,
    actions: [
      { label: 'Go Home', href: '/', variant: 'primary' as const },
      { label: 'Contact Support', href: '/support', variant: 'secondary' as const },
    ],
  },
  401: {
    title: 'Authentication Required',
    description: 'Please log in to access this page.',
    illustration: <UnauthorizedIllustration />,
    actions: [
      { label: 'Log In', href: '/login', variant: 'primary' as const },
      { label: 'Go Home', href: '/', variant: 'secondary' as const },
    ],
  },
};

export const ErrorPage = React.forwardRef<HTMLDivElement, ErrorPageProps>(
  (
    {
      code = 404,
      title,
      description,
      illustration,
      actions,
      showDetails = false,
      error,
      className,
    },
    ref
  ) => {
    const template = errorTemplates[code as keyof typeof errorTemplates] || errorTemplates[404];
    const finalTitle = title || template.title;
    const finalDescription = description || template.description;
    const finalIllustration = illustration || template.illustration;
    const finalActions = actions || template.actions;

    const isDevelopment =
      typeof process !== 'undefined' && process.env && process.env['NODE_ENV'] === 'development';

    return (
      <div
        ref={ref}
        className={cn(
          'min-h-screen flex items-center justify-center p-4',
          'bg-base-100',
          className
        )}
      >
        <div className="max-w-md w-full text-center relative">
          {/* Illustration - Absolutely positioned and centered */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-base-content">{finalIllustration}</div>
          </div>

          {/* Content - Relative positioned to appear above illustration */}
          <div className="relative z-10">
            {/* Error Code */}
            <div className="text-6xl font-bold text-base-content/80 mb-4">{code}</div>

            {/* Title */}
            <h1 className="text-2xl font-bold mb-2 text-base-content">{finalTitle}</h1>

            {/* Description */}
            <p className="text-base-content/70 mb-8">{finalDescription}</p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {finalActions.map((action, index) => {
                if ('href' in action && action.href) {
                  return (
                    <a
                      key={index}
                      href={action.href}
                      className={cn(
                        'btn',
                        action.variant && `btn-${action.variant}`,
                        'inline-flex items-center justify-center gap-2'
                      )}
                    >
                      {'icon' in action && action.icon && action.icon}
                      {action.label}
                    </a>
                  );
                }
                return (
                  <Button
                    key={index}
                    variant={action.variant || 'primary'}
                    onClick={'onClick' in action ? action.onClick : undefined}
                    className="inline-flex items-center justify-center gap-2"
                  >
                    {'icon' in action && action.icon && action.icon}
                    {action.label}
                  </Button>
                );
              })}
            </div>

            {/* Error Details (dev mode) */}
            {showDetails && error && isDevelopment && (
              <details className="mt-8 text-left">
                <summary className="cursor-pointer text-sm text-base-content/70 hover:text-base-content">
                  Technical Details
                </summary>
                <pre className="mt-2 p-4 bg-base-200 rounded text-xs overflow-auto max-h-64">
                  {error.stack || error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>
    );
  }
);

ErrorPage.displayName = 'ErrorPage';
